// Lightweight performance measurement for the SPA.
//
// Two kinds of data are collected and funneled through a single `reportMetric`
// sink:
//   1. Core Web Vitals (LCP, INP, CLS, FCP, TTFB) via the `web-vitals` library.
//   2. Custom User-Timing measures for the embedded admin-portal auth → topic
//      flow (handshake, token → login, login → topic, and end-to-end).
//
// The sink logs (when REACT_APP_LOGGING_ENABLED=true) and dispatches an
// `angkas-perf` CustomEvent on the global scope. Subscribe with
// `onPerfMetric(cb)` to forward metrics to a real RUM backend / beacon later
// without touching the collection code.

import { onCLS, onFCP, onINP, onLCP, onTTFB } from 'web-vitals';

import { LOGGING_ENABLED } from '../config.js';

/** Event dispatched on globalThis for every reported metric. */
export const PERF_EVENT = 'angkas-perf';

/** User-Timing marks for the embedded auth → topic flow. */
export const PERF_MARKS = {
  READY_SENT: 'angkas:messenger-ready-sent',
  TOKEN_RECEIVED: 'angkas:auth-token-received',
  LOGIN_SUCCESS: 'angkas:login-success',
  TOPIC_RENDERED: 'angkas:topic-rendered',
};

const supportsPerf =
  typeof performance !== 'undefined' && typeof performance.mark === 'function';

const seenMarks = new Set();
let flowFinalized = false;

// Round to at most 2 decimal places. Integer-only rounding collapsed small
// scores like CLS (e.g. 0.03) to 0, hiding real layout shift.
function round2(value) {
  return Math.round(value * 100) / 100;
}

function reportMetric(metric) {
  if (LOGGING_ENABLED) {
    const value = typeof metric.value === 'number' ? round2(metric.value) : metric.value;
    console.info('[Perf]', metric.name, value, metric.rating ? `(${metric.rating})` : '');
  }
  try {
    globalThis.dispatchEvent(new CustomEvent(PERF_EVENT, { detail: metric }));
  } catch (err) {
    if (LOGGING_ENABLED) {
      console.warn('[Perf] could not dispatch metric event', err);
    }
  }
}

/**
 * Subscribe to every reported metric (Web Vitals + custom flow measures).
 * Returns an unsubscribe function.
 */
export function onPerfMetric(callback) {
  const handler = event => callback(event.detail);
  globalThis.addEventListener(PERF_EVENT, handler);
  return () => globalThis.removeEventListener(PERF_EVENT, handler);
}

/**
 * Record a User-Timing mark at most once, keeping the first occurrence of each
 * stage. Safe to call from hot paths and in environments without the API.
 */
export function markOnce(name) {
  if (!supportsPerf || seenMarks.has(name)) {
    return;
  }
  seenMarks.add(name);
  try {
    performance.mark(name);
  } catch (err) {
    if (LOGGING_ENABLED) {
      console.warn('[Perf] mark failed', name, err);
    }
  }
}

// Emit a measure between two marks; silently skips when either mark is absent
// (e.g. the handshake marks only exist when running embedded in the portal).
function measureBetween(measureName, startMark, endMark) {
  if (!supportsPerf || !seenMarks.has(startMark) || !seenMarks.has(endMark)) {
    return;
  }
  try {
    const measure = performance.measure(measureName, startMark, endMark);
    const value = typeof measure?.duration === 'number' ?
      measure.duration :
      performance.getEntriesByName(measureName, 'measure').pop()?.duration;
    if (typeof value === 'number') {
      reportMetric({ name: measureName, value, kind: 'custom' });
    }
  } catch (err) {
    if (LOGGING_ENABLED) {
      console.warn('[Perf] measure failed', measureName, err);
    }
  }
}

/**
 * Compute the auth → topic timings once the terminal (topic-rendered) mark has
 * landed. Runs once per session.
 */
export function finalizeAuthTopicFlow() {
  if (flowFinalized || !seenMarks.has(PERF_MARKS.TOPIC_RENDERED)) {
    return;
  }
  flowFinalized = true;
  measureBetween('angkas:handshake', PERF_MARKS.READY_SENT, PERF_MARKS.TOKEN_RECEIVED);
  measureBetween('angkas:token-to-login', PERF_MARKS.TOKEN_RECEIVED, PERF_MARKS.LOGIN_SUCCESS);
  measureBetween('angkas:login-to-topic', PERF_MARKS.LOGIN_SUCCESS, PERF_MARKS.TOPIC_RENDERED);
  measureBetween('angkas:ready-to-topic', PERF_MARKS.READY_SENT, PERF_MARKS.TOPIC_RENDERED);
}

/**
 * Mark the point where the user is fully loaded into a topic and finalize the
 * end-to-end auth → topic measurement. Idempotent.
 */
export function markTopicRendered() {
  markOnce(PERF_MARKS.TOPIC_RENDERED);
  finalizeAuthTopicFlow();
}

/** Register Web Vitals collectors. Call once at app bootstrap. */
export function initWebVitals() {
  onCLS(reportMetric);
  onFCP(reportMetric);
  onINP(reportMetric);
  onLCP(reportMetric);
  onTTFB(reportMetric);
}
